import classNames from "classnames";
import CiroBreadCrumbs from "../../components/shared/CiroBreadCrumbs";
import CiroContainer from "../../components/shared/CiroContainer";
import CiroTitle from "../../components/shared/CiroTitle";
import {
  InternalAutopilotCuratedSearch_AutopilotCuratedSearchQuery,
  InternalAutopilotCuratedSearch_AutopilotCuratedSearchQueryVariables,
  InternalAutopilotCuratedSearch_UpsertAutopilotCuratedSearchMutation,
  InternalAutopilotCuratedSearch_UpsertAutopilotCuratedSearchMutationVariables,
  InternalCuratedSearchFilters,
} from "../../__generated__/graphql";
import { gql, useMutation, useQuery } from "@apollo/client";
import AutopilotCuratedSearchContext from "../../contexts/AutopilotCuratedSearchContext";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import { hasFiltersApplied } from "../../components/autopilotCuratedSearch/curatedSearchUtils";
import CuratedSearchCriteriaAndFiltersContainer from "../../components/autopilotCuratedSearch/CuratedSearchCriteriaAndFiltersContainer";
import CuratedSearchFiltersHeader from "../../components/autopilotCuratedSearch/CuratedSearchFiltersHeader";
import AutopilotCuratedInternalSearchContext, {
  AutopilotCuratedInternalSearchContext_InternalSearchFilters,
} from "../../contexts/AutopilotCuratedInternalSearchContext";
import AutopilotPreviewSteps from "../../components/autopilotPreview/AutopilotPreviewSteps";

const InternalAutopilotCuratedSearch_UpsertAutopilotCuratedSearch = gql`
  mutation InternalAutopilotCuratedSearch_UpsertAutopilotCuratedSearch(
    $input: UpsertAutopilotCuratedSearchInput!
  ) {
    upsertAutopilotCuratedSearch(input: $input) {
      success
      autopilotCuratedSearch {
        id
        internalCuratedSearch {
          filters {
            ...AutopilotCuratedInternalSearchContext_InternalSearchFilters
          }
        }
      }
    }
  }
  ${AutopilotCuratedInternalSearchContext_InternalSearchFilters}
`;

const InternalAutopilotCuratedSearch_AutopilotCuratedSearch = gql`
  query InternalAutopilotCuratedSearch_AutopilotCuratedSearch(
    $id: Int
    $skip: Boolean!
  ) {
    personas {
      id
      name
      description
      source
    }
    autopilotCuratedSearch(id: $id) @skip(if: $skip) {
      id
      prompt
      internalCuratedSearch {
        filters {
          ...AutopilotCuratedInternalSearchContext_InternalSearchFilters
        }
      }
    }
    autopilotCuratedSearches {
      id
      prompt
    }
  }
  ${AutopilotCuratedInternalSearchContext_InternalSearchFilters}
`;

const InternalAutopilotCuratedSearch = ({
  onboarding = false,
}: {
  onboarding?: boolean;
}) => {
  const { startingCuratedSearchId } = useParams();

  const [curatedSearchId, setCuratedSearchId] = useState<number | null>(
    startingCuratedSearchId ? parseInt(startingCuratedSearchId) : null,
  );

  const {
    data: autopilotCuratedSearchData,
    loading: autopilotCuratedSearchLoading,
  } = useQuery<
    InternalAutopilotCuratedSearch_AutopilotCuratedSearchQuery,
    InternalAutopilotCuratedSearch_AutopilotCuratedSearchQueryVariables
  >(InternalAutopilotCuratedSearch_AutopilotCuratedSearch, {
    variables: {
      id: curatedSearchId,
      skip: !curatedSearchId,
    },
    fetchPolicy: "network-only",
  });

  const [savedFilters, setSavedFilters] =
    useState<InternalCuratedSearchFilters | null>(null);

  // Separate effect for initial load from the API
  useEffect(() => {
    // Only set filters from API data when we don't have any filters yet
    // and when we're not in the middle of user edits
    if (
      !savedFilters &&
      autopilotCuratedSearchData?.autopilotCuratedSearch?.internalCuratedSearch
        ?.filters
    ) {
      setSavedFilters(
        autopilotCuratedSearchData.autopilotCuratedSearch.internalCuratedSearch
          .filters,
      );
    }
  }, [autopilotCuratedSearchData, savedFilters]);

  const [
    upsertAutopilotCuratedSearch,
    { loading: upsertAutopilotCuratedSearchLoading },
  ] = useMutation<
    InternalAutopilotCuratedSearch_UpsertAutopilotCuratedSearchMutation,
    InternalAutopilotCuratedSearch_UpsertAutopilotCuratedSearchMutationVariables
  >(InternalAutopilotCuratedSearch_UpsertAutopilotCuratedSearch);

  const [selectedPrompt, setSelectedPrompt] = useState<string>("");
  const [startedGeneratingFilters, setStartedGeneratingFilters] =
    useState(false);
  const [startedPreviewingResults, setStartedPreviewingResults] =
    useState(false);

  useEffect(() => {
    if (!startedGeneratingFilters) {
      setSelectedPrompt("");
      setCuratedSearchId(null);
      setfiltersAndCriteriaMinimized(false);
    }
  }, [startedGeneratingFilters]);

  const [filtersAndCriteriaMinimized, setfiltersAndCriteriaMinimized] =
    useState(false);

  const onSaveFilters = async ({
    currentFilters,
    additionalFiltering,
  }: {
    currentFilters: InternalCuratedSearchFilters;
    additionalFiltering: string[];
  }) => {
    setStartedPreviewingResults(false);
    if (!selectedPrompt) {
      toast.error("No search found");
      return;
    }
    if (
      !currentFilters ||
      !hasFiltersApplied({ filters: currentFilters, type: "internal" })
    ) {
      toast.error("No filters applied");
      return;
    }

    // Save the current filters and scoring criteria before making the API call
    // This preserves them in case the response tries to overwrite them
    setSavedFilters(currentFilters);

    toast.success("Saving your search...");
    await upsertAutopilotCuratedSearch({
      variables: {
        input: {
          id: curatedSearchId,
          promptText: selectedPrompt,
          scoringCriteria: additionalFiltering.map((c) => ({
            criterion: c,
          })),
          internalSearchFilters: {
            companyKeywords: currentFilters?.companyKeywords,
            jobTitles: currentFilters?.jobTitles,
            industries: currentFilters?.industries?.map((i) => ({
              label: i.label,
            })),
            geographies: currentFilters?.geographies?.map((g) => ({
              id: g.id,
              label: g.label,
            })),
            companyHQLocations: currentFilters?.companyHQLocations?.map(
              (c) => ({
                id: c.id,
                label: c.label,
              }),
            ),
            companyHeadCountRange: {
              lowerBound: currentFilters?.companyHeadCountRange?.lowerBound,
              upperBound: currentFilters?.companyHeadCountRange?.upperBound,
            },
            companyNames: currentFilters?.companyNames?.map((c) => ({
              label: c.label,
            })),
            // TODO: Add back profile summary keywords when Proxycurl gets us an answer
            // on better indexing (CIR-3434)
            // profileSummaryKeywords: currentFilters?.profileSummaryKeywords,
            profileSummaryKeywords: null,
          },
        },
      },
      onCompleted: async (data) => {
        setCuratedSearchId(
          data.upsertAutopilotCuratedSearch.autopilotCuratedSearch?.id ?? null,
        );
        setfiltersAndCriteriaMinimized(true);
        setStartedPreviewingResults(true);
      },
    });
  };

  return (
    <AutopilotCuratedSearchContext.Provider
      value={{
        historicalPrompts: (autopilotCuratedSearchData?.autopilotCuratedSearches
          ?.map((c) => c.prompt)
          .filter(Boolean) ?? []) as string[],
        personas: autopilotCuratedSearchData?.personas ?? [],
        selectedPrompt: selectedPrompt,
        setSelectedPrompt: (prompt) => {
          setSelectedPrompt(prompt || "");
        },
        startedGeneratingFilters,
        setStartedGeneratingFilters,
        upsertAutopilotCuratedSearchLoading,
        reset: () => {
          setSelectedPrompt("");
          setCuratedSearchId(null);
          setfiltersAndCriteriaMinimized(false);
          setStartedGeneratingFilters(false);
          setStartedPreviewingResults(false);
        },
      }}
    >
      <AutopilotCuratedInternalSearchContext.Provider
        value={{
          onSaveFilters,
        }}
      >
        <CiroContainer className={classNames("bg-zinc-100")}>
          <CiroBreadCrumbs
            values={[
              "Autopilot",
              onboarding ? "Start searching" : "Search Ciro Database",
            ]}
            href="/autopilot"
          />
          <CiroTitle
            title={onboarding ? "Start searching" : "Search Ciro Database"}
          />
          <CuratedSearchFiltersHeader
            filtersAndCriteriaMinimized={filtersAndCriteriaMinimized}
            setfiltersAndCriteriaMinimized={setfiltersAndCriteriaMinimized}
            currentFilters={savedFilters}
            canMinimize={Boolean(curatedSearchId)}
          />

          <CuratedSearchCriteriaAndFiltersContainer
            filtersAndCriteriaMinimized={filtersAndCriteriaMinimized}
            loading={autopilotCuratedSearchLoading}
          />

          {startedPreviewingResults && (
            <AutopilotPreviewSteps curatedSearchId={curatedSearchId} />
          )}
        </CiroContainer>
      </AutopilotCuratedInternalSearchContext.Provider>
    </AutopilotCuratedSearchContext.Provider>
  );
};

export default InternalAutopilotCuratedSearch;
